import { buildSearchQuery } from "./searchQuery";

/** sessionStorage key used so CompanyList can hydrate the first search. */
export const ONBOARDING_SEARCH_KEY = "onboarding-first-search";

/** After login, send the user to the hydrated Search page. */
export const POST_LOGIN_REDIRECT_KEY = "postLoginRedirect";
const PENDING_COMPLETION_PREFIX = "onboarding-pending-completion:";

const completionKey = (email) => {
  const identity = String(email || "").trim().toLowerCase();
  return identity ? `${PENDING_COMPLETION_PREFIX}${identity}` : null;
};

export const persistPendingOnboardingCompletion = (email, answers) => {
  const key = completionKey(email);
  if (!key || !answers?.workspace_id) return false;
  try {
    sessionStorage.setItem(key, JSON.stringify(answers));
    return true;
  } catch (_error) {
    return false;
  }
};

export const readPendingOnboardingCompletion = (email) => {
  const key = completionKey(email);
  if (!key) return null;
  try {
    const answers = JSON.parse(sessionStorage.getItem(key) || "null");
    return answers?.workspace_id && answers?.search_name && answers?.query
      ? answers
      : null;
  } catch (_error) {
    return null;
  }
};

export const clearPendingOnboardingCompletion = (email) => {
  const key = completionKey(email);
  if (!key) return;
  try {
    sessionStorage.removeItem(key);
  } catch (_error) {
    // A storage failure must not block navigation after durable completion.
  }
};

/** Only an explicitly unfinished profile enters authenticated onboarding. */
export const shouldRouteToOnboarding = (profile) => Boolean(
  profile
  && Object.prototype.hasOwnProperty.call(profile, "onboarding_completed_at")
  && profile.onboarding_completed_at === null
);

export const buildOnboardingSearchQuery = ({
  industryInclude = [],
  locations = [],
  revenueLowerBound = null,
  revenueUpperBound = null,
} = {}) =>
  buildSearchQuery({
    termsInclude: [],
    termsExclude: [],
    industryInclude,
    industryExclude: [],
    locations,
    revenueLowerBound,
    revenueUpperBound,
    employeesLowerBound: null,
    employeesUpperBound: null,
    naturalQuery: "",
    hasNaturalQuery: false,
  });

/**
 * Persist the first-search query and remember the Search URL for post-login redirect.
 */
export const persistOnboardingSearch = (answers) => {
  const query = buildOnboardingSearchQuery(answers);
  const path = `/lists/companies/${ONBOARDING_SEARCH_KEY}`;

  sessionStorage.setItem(
    ONBOARDING_SEARCH_KEY,
    JSON.stringify({
      query,
      name: answers.searchName || "",
    })
  );
  sessionStorage.setItem(POST_LOGIN_REDIRECT_KEY, path);

  return { query, path };
};

export const consumePostLoginRedirect = () => {
  const path = sessionStorage.getItem(POST_LOGIN_REDIRECT_KEY);
  if (!path) return null;
  sessionStorage.removeItem(POST_LOGIN_REDIRECT_KEY);
  return path;
};

export const clearPostLoginRedirect = () => {
  sessionStorage.removeItem(POST_LOGIN_REDIRECT_KEY);
};

/** Discard the previous user's synthetic search only on explicit logout. */
export const clearOnboardingSearch = () => {
  try {
    sessionStorage.removeItem(ONBOARDING_SEARCH_KEY);
  } catch (_error) {
    // Attempt redirect cleanup independently if payload storage is unavailable.
  }
  try {
    if (sessionStorage.getItem(POST_LOGIN_REDIRECT_KEY) === `/lists/companies/${ONBOARDING_SEARCH_KEY}`) {
      sessionStorage.removeItem(POST_LOGIN_REDIRECT_KEY);
    }
  } catch (_error) {
    // Storage may be unavailable; do not prevent local logout.
  }
  try {
    const pendingKeys = [];
    for (let index = 0; index < sessionStorage.length; index += 1) {
      const key = sessionStorage.key(index);
      if (key?.startsWith(PENDING_COMPLETION_PREFIX)) pendingKeys.push(key);
    }
    pendingKeys.forEach((key) => sessionStorage.removeItem(key));
  } catch (_error) {
    // Do not block logout when storage is unavailable.
  }
};
